/**
 * WEBAPP-17: piano serie piramidali (solo set_type = piramidale).
 */

export function isPyramidExercise(exercise) {
    return exercise?.set_type === 'piramidale';
}

export function parseSeriesPlan(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return normalizeSeriesPlanArray(raw);
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? normalizeSeriesPlanArray(parsed) : [];
        } catch {
            return [];
        }
    }
    return [];
}

function normalizeSeriesPlanArray(arr) {
    return arr
        .map((item, idx) => ({
            n: Number(item?.n ?? idx + 1),
            reps: Number(item?.reps ?? item?.ripetizioni ?? 0),
            weight: item?.weight != null && item?.weight !== ''
                ? Number(item.weight)
                : item?.peso != null && item?.peso !== ''
                    ? Number(item.peso)
                    : null,
        }))
        .sort((a, b) => a.n - b.n);
}

export function buildDefaultSeriesPlan(serie, defaultReps, defaultWeight = null) {
    const count = Math.max(1, Number(serie) || 3);
    const reps = Math.max(1, Number(defaultReps) || 10);
    return Array.from({ length: count }, (_, i) => ({
        n: i + 1,
        reps,
        weight: defaultWeight != null && defaultWeight !== '' ? Number(defaultWeight) : null,
    }));
}

export function ensureSeriesPlan(exercise) {
    if (!isPyramidExercise(exercise)) {
        return null;
    }
    const normalized = {
        ...exercise,
        ripetizioni: exercise.ripetizioni ?? exercise.ripetizioni_max,
        peso: exercise.peso ?? exercise.peso_suggerito,
    };
    const serie = Math.max(1, Number(normalized.serie) || 3);
    let plan = parseSeriesPlan(normalized.series_plan);
    if (plan.length !== serie) {
        const last = plan[plan.length - 1];
        const fallbackReps = (last?.reps ?? Number(normalized.ripetizioni)) || 10;
        const fallbackWeight = last?.weight ?? (Number(normalized.peso) || null);
        plan = buildDefaultSeriesPlan(serie, fallbackReps, fallbackWeight);
    }
    return plan;
}

export function resizeSeriesPlan(plan, newSerie, fallbackReps, fallbackWeight = null) {
    const count = Math.max(1, Number(newSerie) || 1);
    const current = Array.isArray(plan) ? [...plan] : [];
    const last = current[current.length - 1];
    const reps = last?.reps ?? fallbackReps ?? 10;
    const weight = last?.weight ?? fallbackWeight ?? null;

    if (current.length === count) {
        return current.map((row, i) => ({ ...row, n: i + 1 }));
    }
    if (current.length > count) {
        return current.slice(0, count).map((row, i) => ({ ...row, n: i + 1 }));
    }
    const out = current.map((row, i) => ({ ...row, n: i + 1 }));
    while (out.length < count) {
        out.push({ n: out.length + 1, reps, weight });
    }
    return out;
}

/** Sintesi compatta: «12 → 10 → 8 → 6». */
export function formatSeriesPlanSummary(plan) {
    if (!plan?.length) return '';
    return plan.map((r) => r.reps).join(' → ');
}

function formatWeightDisplay(weight) {
    const n = Number(weight);
    if (!Number.isFinite(n) || n <= 0) return '0';
    if (Number.isInteger(n)) return String(n);
    const fixed = n.toFixed(2);
    return fixed.replace(/\.?0+$/, '');
}

/** Sintesi pesi piramidali: «40 → 45 → 50». */
export function formatSeriesPlanWeightSummary(plan, fallbackWeight = 0) {
    if (!plan?.length) return '';
    return plan
        .map((r) => {
            const w =
                r.weight != null && r.weight !== ''
                    ? Number(r.weight)
                    : Number(fallbackWeight) || 0;
            return formatWeightDisplay(w);
        })
        .join(' → ');
}

export function pyramidDisplayReps(exercise) {
    if (!isPyramidExercise(exercise)) return null;
    const plan = ensureSeriesPlan(exercise);
    return formatSeriesPlanSummary(plan);
}

export function pyramidDisplayWeight(exercise) {
    if (!isPyramidExercise(exercise)) return null;
    const plan = ensureSeriesPlan(exercise);
    return formatSeriesPlanWeightSummary(plan, exercise.peso);
}

/** Target peso per visualizzazione scheda (singolo o piramidale). */
export function formatWorkoutWeightTarget(exercise) {
    if (!exercise) return '0';
    const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;
    if (isCardio) return '0.00';

    const pyramid = pyramidDisplayWeight(exercise);
    if (pyramid) return pyramid;

    const peso = Number(exercise.peso);
    if (!peso || peso <= 0) return '0';
    return formatWeightDisplay(peso);
}

/** Target reps per serie k (1-based). */
export function repsForPyramidSeries(exercise, seriesNumber) {
    const plan = ensureSeriesPlan(exercise);
    if (!plan?.length) return Number(exercise.ripetizioni) || 10;
    const idx = Math.max(0, Math.min(plan.length - 1, seriesNumber - 1));
    return (plan[idx]?.reps ?? Number(exercise.ripetizioni)) || 10;
}

/** Target peso per serie k (1-based). */
export function weightForPyramidSeries(exercise, seriesNumber) {
    const plan = ensureSeriesPlan(exercise);
    if (!plan?.length) return Number(exercise.peso) || 0;
    const idx = Math.max(0, Math.min(plan.length - 1, seriesNumber - 1));
    const w = plan[idx]?.weight;
    return w != null && w !== '' ? Number(w) : Number(exercise.peso) || 0;
}
